import { Button, DatePicker, Input, Select, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

import type { PortfolioFilterOptions, SowStatus } from '@/api/portfolio/api';
import {
  HEALTH_COLOR_LABEL,
  HEALTH_FILTER_OPTIONS,
  HealthDot,
  HealthOptionLabel,
  type HealthColor,
  type HealthFilterValue,
} from '@/components/common/HealthIndicator';
import type {
  PortfolioFilters as PortfolioFilterState,
  PortfolioHealthFilters,
} from '@/hooks/usePortfolio';

const { Text } = Typography;

const SOW_STATUS_LABEL: Record<SowStatus | 'all', string> = {
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
  draft: 'Draft',
  pending: 'Pending',
  expired: 'Expired',
  closed: 'Closed',
};

/** The four Health dropdowns (More filters → Health), in display order. */
const HEALTH_FIELDS: { key: keyof PortfolioHealthFilters; label: string }[] = [
  { key: 'overall_status', label: 'Status' },
  { key: 'on_schedule', label: 'On Schedule' },
  { key: 'csat', label: 'CSAT' },
  { key: 'team_health', label: 'Team Health' },
];

export interface PortfolioFiltersProps {
  options: PortfolioFilterOptions;
  filters: PortfolioFilterState;
  hideClientFilter?: boolean;
  onClientChange: (value: string[]) => void;
  onProjectChange: (value: string[]) => void;
  onOfferingChange: (value: string[]) => void;
  onLeaderChange: (value: string[]) => void;
  onSowStatusChange: (value: SowStatus | 'all') => void;
  onHealthChange: (value: Partial<PortfolioHealthFilters>) => void;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (value: {
    starts_on_before?: string | null;
    ends_on_after?: string | null;
  }) => void;
  onClearAll: () => void;
}

interface ActiveChip {
  key: string;
  label: string;
  onRemove: () => void;
  /** When set, a colored health dot renders in front of the chip label. */
  ryg?: HealthColor;
}

const SEARCH_DEBOUNCE_MS = 300;

/** Filter bar with active-filter chips and a collapsible "More filters" section. */
const PortfolioFiltersComponent: React.FC<PortfolioFiltersProps> = ({
  options,
  filters,
  hideClientFilter,
  onClientChange,
  onProjectChange,
  onOfferingChange,
  onLeaderChange,
  onSowStatusChange,
  onHealthChange,
  onSearchChange,
  onDateRangeChange,
  onClearAll,
}) => {
  const hasHealthFilter = HEALTH_FIELDS.some((field) => filters[field.key] !== 'all');
  const [showMore, setShowMore] = useState<boolean>(
    Boolean(filters.starts_on_before || filters.ends_on_after) || hasHealthFilter,
  );

  // Local draft keeps typing instant while the actual query (and the page-reset
  // + refetch it triggers) is debounced, so keystrokes don't churn the table.
  const [searchDraft, setSearchDraft] = useState<string>(filters.project_name);
  // Reflect external resets (chip removal / "Clear all") back into the input.
  useEffect(() => {
    setSearchDraft(filters.project_name);
  }, [filters.project_name]);
  useEffect(() => {
    if (searchDraft === filters.project_name) {
      return undefined;
    }
    const handle = setTimeout(() => onSearchChange(searchDraft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchDraft, filters.project_name, onSearchChange]);

  const clientLabelById = useMemo(
    () => new Map(options.clients.map((client) => [client.value, client.label])),
    [options.clients],
  );
  const projectLabelById = useMemo(
    () => new Map(options.projects.map((project) => [project.value, project.label])),
    [options.projects],
  );

  const chips = useMemo<ActiveChip[]>(() => {
    const next: ActiveChip[] = [];
    if (filters.sow_status !== 'all') {
      next.push({
        key: 'sow',
        label: `SOW: ${SOW_STATUS_LABEL[filters.sow_status]}`,
        onRemove: () => onSowStatusChange('all'),
      });
    }
    if (!hideClientFilter) {
      for (const clientId of filters.client_id) {
        next.push({
          key: `client-${clientId}`,
          label: `Client: ${clientLabelById.get(clientId) ?? clientId}`,
          onRemove: () => onClientChange(filters.client_id.filter((id) => id !== clientId)),
        });
      }
    }
    for (const projectId of filters.project_id) {
      next.push({
        key: `project-${projectId}`,
        label: `Project: ${projectLabelById.get(projectId) ?? projectId}`,
        onRemove: () => onProjectChange(filters.project_id.filter((id) => id !== projectId)),
      });
    }
    for (const offering of filters.offering) {
      next.push({
        key: `offering-${offering}`,
        label: `Offering: ${offering}`,
        onRemove: () => onOfferingChange(filters.offering.filter((item) => item !== offering)),
      });
    }
    for (const leader of filters.leader) {
      next.push({
        key: `leader-${leader}`,
        label: `Leader: ${leader}`,
        onRemove: () => onLeaderChange(filters.leader.filter((item) => item !== leader)),
      });
    }
    for (const field of HEALTH_FIELDS) {
      const value = filters[field.key];
      if (value !== 'all') {
        next.push({
          key: `health-${field.key}`,
          label: `${field.label}: ${HEALTH_COLOR_LABEL[value]}`,
          onRemove: () => onHealthChange({ [field.key]: 'all' }),
          ryg: value,
        });
      }
    }
    if (filters.project_name.trim()) {
      next.push({
        key: 'search',
        label: `Search: ${filters.project_name.trim()}`,
        onRemove: () => onSearchChange(''),
      });
    }
    if (filters.starts_on_before) {
      next.push({
        key: 'starts',
        label: `Starts on/before ${dayjs(filters.starts_on_before).format('MMM D, YYYY')}`,
        onRemove: () => onDateRangeChange({ starts_on_before: null }),
      });
    }
    if (filters.ends_on_after) {
      next.push({
        key: 'ends',
        label: `Ends on/after ${dayjs(filters.ends_on_after).format('MMM D, YYYY')}`,
        onRemove: () => onDateRangeChange({ ends_on_after: null }),
      });
    }
    return next;
  }, [
    filters,
    hideClientFilter,
    clientLabelById,
    projectLabelById,
    onSowStatusChange,
    onClientChange,
    onProjectChange,
    onOfferingChange,
    onLeaderChange,
    onHealthChange,
    onSearchChange,
    onDateRangeChange,
  ]);

  return (
    <div className="pf-filters">
      <div className="pf-filters__row">
        <Text className="pf-filters__scope">Scope</Text>

        {!hideClientFilter && (
          <Select
            mode="multiple"
            allowClear
            className="pf-filters__control"
            placeholder="Client — All"
            maxTagCount={1}
            value={filters.client_id}
            onChange={onClientChange}
            options={options.clients.map((client) => ({
              value: client.value,
              label: client.label,
            }))}
          />
        )}
        <Select
          mode="multiple"
          allowClear
          className="pf-filters__control"
          placeholder="Project — All"
          maxTagCount={1}
          value={filters.project_id}
          onChange={onProjectChange}
          optionFilterProp="label"
          options={options.projects.map((project) => ({
            value: project.value,
            label: project.label,
          }))}
        />
        <Select
          mode="multiple"
          allowClear
          className="pf-filters__control"
          placeholder="Offering — All"
          maxTagCount={1}
          value={filters.offering}
          onChange={onOfferingChange}
          options={options.offerings.map((offering) => ({ value: offering, label: offering }))}
        />
        <Select
          mode="multiple"
          allowClear
          className="pf-filters__control"
          placeholder="Leader — All"
          maxTagCount={1}
          value={filters.leader}
          onChange={onLeaderChange}
          options={options.leaders.map((leader) => ({ value: leader, label: leader }))}
        />
        <Select
          className="pf-filters__control pf-filters__control--sow"
          value={filters.sow_status}
          onChange={onSowStatusChange}
          options={[
            { value: 'all', label: 'SOW: All' },
            ...options.sowStatuses.map((status) => ({
              value: status,
              label: `SOW: ${SOW_STATUS_LABEL[status]}`,
            })),
          ]}
        />
        <Input.Search
          allowClear
          className="pf-filters__search"
          placeholder="Search client, project, ID…"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
        />
        <Button
          type="link"
          className="pf-filters__more"
          onClick={() => setShowMore((prev) => !prev)}
        >
          {showMore ? '– Fewer filters' : '+ More filters'}
        </Button>
      </div>

      {showMore ? (
        <div className="pf-filters__more-panel">
          <div className="pf-filters__section">
            <Text className="pf-filters__section-label">Health</Text>
            <Space wrap size="middle" className="pf-filters__section-controls">
              {HEALTH_FIELDS.map((field) => (
                <span key={field.key} className="pf-filters__field">
                  <Text type="secondary">{field.label}</Text>
                  <Select
                    className="pf-filters__health-select"
                    value={filters[field.key] as HealthFilterValue}
                    onChange={(value: HealthFilterValue) => onHealthChange({ [field.key]: value })}
                    options={HEALTH_FILTER_OPTIONS}
                  />
                </span>
              ))}
              <span className="pf-filters__field">
                <Text type="secondary">
                  On Budget <span className="pf-col-badge">V2</span>
                </Text>
                <Tooltip title="Coming in V2">
                  <Select
                    disabled
                    className="pf-filters__health-select"
                    value="all"
                    options={[{ value: 'all', label: <HealthOptionLabel value="all" /> }]}
                  />
                </Tooltip>
              </span>
            </Space>
          </div>

          <div className="pf-filters__section pf-filters__dates">
            <Text className="pf-filters__section-label">Dates</Text>
            <Space wrap size="middle" className="pf-filters__section-controls">
              <span className="pf-filters__field">
                <Text type="secondary">Starts on/before</Text>
                <DatePicker
                  value={filters.starts_on_before ? dayjs(filters.starts_on_before) : null}
                  onChange={(date) =>
                    onDateRangeChange({ starts_on_before: date ? date.format('YYYY-MM-DD') : null })
                  }
                />
              </span>
              <span className="pf-filters__field">
                <Text type="secondary">Ends on/after</Text>
                <DatePicker
                  value={filters.ends_on_after ? dayjs(filters.ends_on_after) : null}
                  onChange={(date) =>
                    onDateRangeChange({ ends_on_after: date ? date.format('YYYY-MM-DD') : null })
                  }
                />
              </span>
            </Space>
          </div>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className="pf-filters__chips">
          <Text type="secondary" className="pf-filters__chips-label">
            Active ({chips.length})
          </Text>
          {chips.map((chip) => (
            <Tag key={chip.key} closable onClose={chip.onRemove} className="pf-chip">
              {chip.ryg ? <HealthDot status={chip.ryg} /> : null}
              {chip.label}
            </Tag>
          ))}
          <Button type="link" size="small" className="pf-filters__clear" onClick={onClearAll}>
            Clear all
          </Button>
        </div>
      ) : null}
    </div>
  );
};

/** Memoized so table data refetches don't re-render the whole filter bar. */
export const PortfolioFilters = React.memo(PortfolioFiltersComponent);

export default PortfolioFilters;
